/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Tabs, Typography, message, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import type { ModalProps } from 'antd/lib/modal';
import { queryCache, useMutation, useQuery } from 'react-query';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

import {
  getCurrentCorrectionLetter,
  getCurrentCorrectionLetterPreview,
  getCurrentCorrectionLetterReportPreview,
  manageCurrentCorrectionLetterMovements,
  manageCurrentCorrectionLetterQuestions,
  updateCurrentCorrectionLetter,
  releaseCurrentCorrectionLetter,
  updateInstantMessageDraft,
  sendInstantMessageDraft,
  InstantMessage,
  getInstantMessageDraft,
  getInstantMessage,
  getCorrectionLetter,
  getInstantMessageVendor,
  getCorrectionLetterVendor,
  getSentInstantMessageEmailPreviewHtml,
  getSentCorrectionLetterEmailPreviewHtml,
  getDraftInstantMessageEmailPreviewHtml,
  getSentInstantMessageEmailPreviewHtmlVendor,
  getSentCorrectionLetterEmailPreviewHtmlVendor,
} from '~src/services/api/movements.ts';

import { LetterTab } from '~components/popups-modals/MovementsModals/Tabs/LetterTab.modal';
import { PreviewTab } from '~components/popups-modals/MovementsModals/Tabs/PreviewTab.modal';
import { MovementsTab } from '~components/popups-modals/MovementsModals/Tabs/MovementsTab.modal';
import { QuestionsTab } from '~components/popups-modals/MovementsModals/Tabs/QuestionsTab.modal';
import { appLog } from '~src/utils/log';
import { InstantMessageLetterTab } from '~components/popups-modals/MovementsModals/Tabs/InstantMessageLetterTab.modal';

type CorrectionLetter = {
  id: number;
  title: string;
  description: string;
  status: number;
  movement_ids?: number[];
  comment_ids?: number[]; // backend example shows comment_ids; manage endpoint says question_ids
  question_ids?: number[]; // in case backend returns this instead
  modification_report?: { file_guid: string; name: string } | null;
  attachments?: { file_guid: string; name: string }[];
};

export interface CorrelationLetterModalProps extends ModalProps {
  projectId: number;
  tenderId: number;
  selectedMovementIds?: number[];
  selectedQuestionIds?: number[];
  onOpenFileByGuid?: (fileGuid: string, fileName?: string) => void;
  isInQaTab?: boolean;
  instantMessageDraft?: InstantMessage | null;
  readOnly?: boolean;
  readOnlyData?: any;
  isWinningLetter?: boolean;
  shareId?: string;
  shareKey?: string;
}

export const CorrelationLetterModal: React.FC<CorrelationLetterModalProps> = ({
  onCancel,
  projectId,
  tenderId,
  selectedMovementIds = [],
  selectedQuestionIds = [],
  onOpenFileByGuid,
  isInQaTab = false,
  instantMessageDraft,
  readOnly = false,
  readOnlyData,
  isWinningLetter = false,
  shareId,
  shareKey,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  // ==constants
  const isVendorSide = !!shareId && !!shareKey;
  const visible = !!modalProps.visible;

  const [localDraft, setLocalDraft] = useState<InstantMessage | null>(null);

  useEffect(() => {
    setLocalDraft(instantMessageDraft || null);
  }, [instantMessageDraft]);

  const isInstantMessage = useMemo(() => {
    if (readOnly && readOnlyData) {
      return readOnlyData.type === 5;
    }
    return isInQaTab || isWinningLetter;
  }, [readOnly, readOnlyData, isInQaTab, isWinningLetter]);

  const modalTitle = isWinningLetter
    ? `${intl.formatMessage({ id: 'wining-letter-title-modal' })}`
    : isInstantMessage
    ? (readOnly
        ? `${intl.formatMessage({ id: 'instant-message-preview' })}`
        : `${intl.formatMessage({ id: 'edit-draft' })} ${intl.formatMessage({
            id: 'instant-message',
          })}`) || intl.formatMessage({ id: 'new-message' })
    : intl.formatMessage({ id: 'movement-correction-letter' });

  const correctionLetterQueryKey = useMemo(
    () => ['current_correction_letter', projectId, tenderId],
    [projectId, tenderId]
  );

  // ==state
  const [activeTab, setActiveTab] = useState<
    'letter' | 'movements' | 'questions' | 'preview'
  >('letter');

  const [isLetterDirty, setIsLetterDirty] = useState(false);
  const [letterResetNonce, setLetterResetNonce] = useState(0);

  const didAutoAddMovementsRef = useRef(false);
  const didAutoAddQuestionsRef = useRef(false);
  const letterTabRef = useRef<any>(null);
  const instantMessageTabRef = useRef<any>(null);
  const isSaveAndReleasePending = useRef(false);

  // == queries for Read Only (Preview) mode
  const readOnlyId = readOnlyData?.message_id || readOnlyData?.id;
  const readOnlyType = readOnlyData?.type;

  const fullInstantMessageQuery = useQuery(
    [
      'instant_message',
      isVendorSide ? shareId : projectId,
      isVendorSide ? 'vendor' : tenderId,
      readOnlyId,
    ],
    () =>
      isVendorSide
        ? getInstantMessageVendor({
            shareId: shareId!,
            shareKey: shareKey!,
            instantMessageId: readOnlyId,
          })
        : getInstantMessage({
            projectId,
            tenderId,
            instantMessageId: readOnlyId,
          }),
    {
      enabled: visible && readOnly && readOnlyType === 5 && !!readOnlyId,
      refetchOnWindowFocus: false,
    }
  );

  const fullCorrectionLetterQuery = useQuery(
    [
      'correction_letter',
      isVendorSide ? shareId : projectId,
      isVendorSide ? 'vendor' : tenderId,
      readOnlyId,
    ],
    () =>
      isVendorSide
        ? getCorrectionLetterVendor({
            shareId: shareId!,
            shareKey: shareKey!,
            correctionLetterId: readOnlyId,
          })
        : getCorrectionLetter({
            projectId,
            tenderId,
            correctionLetterId: readOnlyId,
          }),
    {
      enabled: visible && readOnly && readOnlyType === 2 && !!readOnlyId,
      refetchOnWindowFocus: false,
    }
  );

  // ==query (Correction Letter - Draft mode)
  const currentLetterQuery = useQuery<CorrectionLetter>(
    correctionLetterQueryKey,
    () =>
      getCurrentCorrectionLetter({
        project_id: projectId,
        tender_id: tenderId,
      }),
    {
      enabled:
        visible && !isInstantMessage && !readOnly && !!projectId && !!tenderId,
      refetchOnWindowFocus: false,
    }
  );

  // == data mapping for Read Only mode
  const displayLetter = useMemo(() => {
    if (readOnly && readOnlyData) {
      // If we have full data from the new APIs, use it
      if (readOnlyType === 5 && fullInstantMessageQuery.data) {
        return {
          ...fullInstantMessageQuery.data,
          question_ids: [],
          movement_ids: [],
        };
      }

      if (readOnlyType === 2 && fullCorrectionLetterQuery.data) {
        return {
          ...fullCorrectionLetterQuery.data,
          question_ids: fullCorrectionLetterQuery.data.comment_ids ?? [],
          message: fullCorrectionLetterQuery.data.description,
        };
      }

      // Initial fallback (from list data)
      return {
        ...readOnlyData,
        // Map "state" from JSON to "status" used in the modal
        status: readOnlyData.status ?? readOnlyData.state,
        // Map "comment_ids" from JSON to "question_ids"
        question_ids: readOnlyData.question_ids ?? readOnlyData.comment_ids,
        // Map description to message for CorrectionLetter/InstantMessageLetterTab
        message: readOnlyData.message ?? readOnlyData.description,
      } as any;
    }
    return currentLetterQuery.data;
  }, [
    readOnly,
    readOnlyData,
    readOnlyType,
    currentLetterQuery.data,
    fullInstantMessageQuery.data,
    fullCorrectionLetterQuery.data,
  ]);

  // ==mutations (Correction Letter)
  const [doUpdateLetter, { isLoading: isUpdatingLetter }] = useMutation(
    updateCurrentCorrectionLetter,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(correctionLetterQueryKey);
        message.success(intl.formatMessage({ id: 'correction-letter-saved' }));
        setIsLetterDirty(false);
        if (isSaveAndReleasePending.current) {
          isSaveAndReleasePending.current = false;
          confirmRelease(() => {
            doReleaseLetter({
              projectId,
              tenderId,
              lang: intl.locale,
            } as any);
          });
        }
      },
      onError: (error, payload) => {
        isSaveAndReleasePending.current = false;
        message.error(
          intl.formatMessage({ id: 'correction-letter-save-failed' })
        );
        appLog('doUpdateLetter -> payload', payload);
        appLog('doUpdateLetter -> error', error);
      },
    }
  );

  const [doManageMovements, { isLoading: isManagingMovements }] = useMutation(
    manageCurrentCorrectionLetterMovements,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(correctionLetterQueryKey);
        message.success(
          intl.formatMessage({ id: 'correction-letter-movements-updated' })
        );
      },
      onError: (error, payload) => {
        message.error(
          intl.formatMessage({
            id: 'correction-letter-movements-update-failed',
          })
        );
        appLog('doManageMovements -> payload', payload);
        appLog('doManageMovements -> error', error);
      },
    }
  );

  const [doManageQuestions, { isLoading: isManagingQuestions }] = useMutation(
    manageCurrentCorrectionLetterQuestions,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(correctionLetterQueryKey);
        message.success(
          intl.formatMessage({ id: 'correction-letter-questions-updated' })
        );
      },
      onError: (error, payload) => {
        message.error(
          intl.formatMessage({
            id: 'correction-letter-questions-update-failed',
          })
        );
        appLog('doManageQuestions -> payload', payload);
        appLog('doManageQuestions -> error', error);
      },
    }
  );

  const [doReleaseLetter, { isLoading: isReleasingLetter }] = useMutation(
    releaseCurrentCorrectionLetter,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(correctionLetterQueryKey);

        queryCache.invalidateQueries([
          'all_tender_messages',
          projectId,
          String(tenderId),
        ]);
        message.success(
          intl.formatMessage({ id: 'correction-letter-released-success' })
        );
        onCancel?.({} as any);
      },
      onError: (error, payload) => {
        message.error(
          intl.formatMessage({ id: 'correction-letter-release-failed' })
        );
        appLog('doReleaseLetter -> payload', payload);
        appLog('doReleaseLetter -> error', error);
      },
    }
  );

  // ==mutations (Instant Message)
  const [doUpdateDraft, { isLoading: isUpdatingDraft }] = useMutation(
    updateInstantMessageDraft,
    {
      // Adding the second argument 'variables'
      onSuccess: (data: any, variables: any) => {
        queryCache.invalidateQueries([
          'instant_message_drafts',
          projectId,
          tenderId,
        ]);
        message.success(intl.formatMessage({ id: 'draft-saved-success' }));
        setIsLetterDirty(false);

        // Merge: old data + form data (variables.body) + server response
        setLocalDraft((prev) => ({
          ...prev,
          ...variables.body,
          ...(data || {}),
        }));

        // Adding logic for SAVE & SEND
        if (isSaveAndReleasePending.current) {
          isSaveAndReleasePending.current = false;
          Modal.confirm({
            title: intl.formatMessage({ id: 'confirm-send-message' }),
            content: intl.formatMessage({ id: 'confirm-send-message-body' }),
            okText: intl.formatMessage({ id: 'send' }),
            cancelText: intl.formatMessage({ id: 'cancel' }),
            onOk: () => {
              doSendDraft({
                projectId,
                tenderId,
                draftId: variables.draftId,
                body: {
                  title: variables.body.title,
                  message: variables.body.message,
                  reference: variables.body.reference,
                },
              });
            },
          });
        }
      },
      onError: () => {
        isSaveAndReleasePending.current = false;
        message.error(intl.formatMessage({ id: 'draft-save-failed' }));
      },
    }
  );

  const [doSendDraft, { isLoading: isSendingDraft }] = useMutation(
    sendInstantMessageDraft,
    {
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'message-sent-success' }));
        queryCache.invalidateQueries([
          'instant_message_drafts',
          projectId,
          tenderId,
        ]);

        queryCache.invalidateQueries([
          'all_tender_messages',
          projectId,
          String(tenderId),
        ]);
        onCancel?.({} as any);
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'message-send-failed' }));
      },
    }
  );

  // ==effects
  useEffect(() => {
    if (visible) {
      if (readOnly && readOnlyData) {
        // Tab routing logic based on type
        if (readOnlyData.type === 2) {
          setActiveTab('letter'); // Correction Letters open in Preview
        } else if (readOnlyData.type === 5) {
          setActiveTab('letter'); // Instant Messages open in Content
        }
      }
    } else {
      // Reset local state when modal closes
      didAutoAddMovementsRef.current = false;
      didAutoAddQuestionsRef.current = false;
      setActiveTab('letter');
      setIsLetterDirty(false);
      setLetterResetNonce(0);
      isSaveAndReleasePending.current = false;
    }
  }, [visible, readOnly, readOnlyData]);

  const confirmDiscardUnsavedChanges = (onDiscard: () => void) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'unsaved-changes' }),
      content: intl.formatMessage({ id: 'unsaved-changes-message' }),
      okText: intl.formatMessage({ id: 'discard' }),
      cancelText: intl.formatMessage({ id: 'stay' }),
      okType: 'danger',
      onOk: () => {
        setLetterResetNonce((n) => n + 1);
        setIsLetterDirty(false);
        onDiscard();
      },
    });
  };

  const confirmRelease = (onOk: () => void) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'correction-letter-release-title' }),
      content: intl.formatMessage({ id: 'correction-letter-release-message' }),
      okText: intl.formatMessage({ id: 'release' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      okType: 'primary',
      onOk,
    });
  };

  const handleRelease = () => {
    if (isInstantMessage) {
      if (!localDraft?.id) return;

      if (isLetterDirty) {
        message.warning(
          intl.formatMessage({ id: 'save-changes-before-sending' })
        );
        return;
      }

      Modal.confirm({
        title: intl.formatMessage({ id: 'confirm-send-message' }),
        content: intl.formatMessage({ id: 'confirm-send-message-body' }),
        okText: intl.formatMessage({ id: 'send' }),
        cancelText: intl.formatMessage({ id: 'cancel' }),
        onOk: () => {
          doSendDraft({
            projectId,
            tenderId,
            draftId: localDraft.id,
            body: {
              title: localDraft.title,
              message: localDraft.message,
              reference: localDraft.reference,
            },
          });
        },
      });
      return;
    }

    // Correction Letter
    if (isLetterDirty) {
      confirmDiscardUnsavedChanges(() => {
        confirmRelease(() => {
          doReleaseLetter({
            projectId,
            tenderId,
            lang: intl.locale, // use current locale
          } as any);
        });
      });
      return;
    }

    confirmRelease(() => {
      doReleaseLetter({
        projectId,
        tenderId,
        lang: intl.locale,
      } as any);
    });
  };

  const handleRequestClose = () => {
    if (isLetterDirty) {
      confirmDiscardUnsavedChanges(() => onCancel?.({} as any));
      return;
    }

    onCancel?.({} as any);
  };

  const handleTabChange = (nextKey: string) => {
    const next = nextKey as any;

    // Only guard leaving the "letter" tab, because dirty state is about Title/Description
    if (activeTab === 'letter' && next !== 'letter' && isLetterDirty) {
      confirmDiscardUnsavedChanges(() => setActiveTab(next));
      return;
    }

    setActiveTab(next);
  };

  useEffect(() => {
    if (!visible || isInstantMessage || readOnly) return;
    if (didAutoAddMovementsRef.current) return;
    if (!selectedMovementIds?.length) return;
    if (!currentLetterQuery.data) return;

    const currentIds = currentLetterQuery.data.movement_ids || [];
    const toAdd = selectedMovementIds.filter((id) => !currentIds.includes(id));

    didAutoAddMovementsRef.current = true;
    if (!toAdd.length) return;

    doManageMovements({
      projectId,
      tenderId,
      action: 'add_movement',
      body: { movement_ids: toAdd },
    });
  }, [
    visible,
    selectedMovementIds,
    currentLetterQuery.data,
    projectId,
    tenderId,
    doManageMovements,
    isInQaTab,
    readOnly,
  ]);

  useEffect(() => {
    if (!visible || isInstantMessage || readOnly) return;
    if (didAutoAddQuestionsRef.current) return;
    if (!selectedQuestionIds?.length) return;
    if (!currentLetterQuery.data) return;

    const currentIds =
      currentLetterQuery.data.question_ids ||
      currentLetterQuery.data.comment_ids ||
      [];

    const toAdd = selectedQuestionIds.filter((id) => !currentIds.includes(id));

    didAutoAddQuestionsRef.current = true;
    if (!toAdd.length) return;

    doManageQuestions({
      projectId,
      tenderId,
      action: 'add_question',
      body: { question_ids: toAdd },
    });
  }, [
    visible,
    selectedQuestionIds,
    currentLetterQuery.data,
    projectId,
    tenderId,
    doManageQuestions,
    isInQaTab,
    readOnly,
  ]);

  const isBusy =
    currentLetterQuery.isLoading ||
    fullInstantMessageQuery.isLoading ||
    fullCorrectionLetterQuery.isLoading ||
    isUpdatingLetter ||
    isManagingMovements ||
    isManagingQuestions ||
    isReleasingLetter ||
    isUpdatingDraft ||
    isSendingDraft;

  const statusLabel = useMemo(() => {
    if (isInstantMessage) return '';
    const st = displayLetter?.status;
    if (st == null) return '';
    return `#${st}`;
  }, [displayLetter?.status, isInstantMessage]);

  const handleSaveAndRelease = () => {
    if (isLetterDirty) {
      isSaveAndReleasePending.current = true;
      if (isInstantMessage) {
        instantMessageTabRef.current?.save?.();
      } else {
        letterTabRef.current?.save?.();
      }
    } else {
      handleRelease();
    }
  };

  const isPreviewDisabled = useMemo(() => {
    if (readOnly) {
      // Sent Correction Letter
      if (readOnlyType === 2 && readOnlyId) return false;
      // Sent Instant Message
      if (readOnlyType === 5 && readOnlyId) return false;
    }

    // Draft Instant Message
    if (isInstantMessage && !localDraft?.id) return true;

    // Draft Correction Letter
    if (!isInstantMessage && !displayLetter?.id) return true;

    return false;
  }, [
    isVendorSide,
    readOnly,
    readOnlyType,
    readOnlyId,
    isInstantMessage,
    localDraft?.id,
    displayLetter?.id,
  ]);

  // ==return
  return (
    <Modal
      title={
        <div css={styles.modalTitle}>
          <div css={styles.modalTitleLeft}>
            <div css={styles.titleText}>{modalTitle}</div>
          </div>
          <Typography.Text type="secondary" css={styles.headerHint}>
            {isInstantMessage
              ? readOnly
                ? intl.formatMessage({
                    id: 'instant-message-recipients-auto-view',
                  })
                : intl.formatMessage({
                    id: 'instant-message-recipients-auto-edit',
                  })
              : readOnly
              ? intl.formatMessage({
                  id: 'correction-letter-recipients-auto-view',
                })
              : intl.formatMessage({ id: 'correction-letter-recipients-auto' })}
          </Typography.Text>
        </div>
      }
      width="min(80vw, 820px)"
      bodyStyle={{ padding: '18px 20px 0 20px' }}
      centered
      destroyOnClose
      maskClosable={false}
      footer={[
        <Fragment key="footer">
          <Button key="close" onClick={handleRequestClose}>
            {intl.formatMessage({ id: 'close' })}
          </Button>

          {/* Instant Message: Send */}
          {!readOnly && isInstantMessage && !isLetterDirty && (
            <Button
              key="release"
              type="primary"
              onClick={handleRelease}
              disabled={isBusy || !localDraft?.id}>
              {intl.formatMessage({ id: 'send' })}
            </Button>
          )}

          {/* Instant Message: Save & Send */}
          {!readOnly && isInstantMessage && isLetterDirty && (
            <Button
              key="save-release"
              type="primary"
              loading={isBusy}
              onClick={handleSaveAndRelease}
              disabled={isBusy || !localDraft?.id}>
              {intl.formatMessage({
                id: 'save-and-send',
                defaultMessage: 'Save & Send',
              })}
            </Button>
          )}

          {/* Correction Letter: Release */}
          {!readOnly && !isInstantMessage && !isLetterDirty && (
            <Button
              key="release"
              type="primary"
              onClick={handleRelease}
              disabled={isBusy || !displayLetter?.id}>
              {intl.formatMessage({ id: 'release' })}
            </Button>
          )}

          {/* Correction Letter: Save & Release */}
          {!readOnly && !isInstantMessage && isLetterDirty && (
            <Button
              key="save-release"
              type="primary"
              loading={isBusy}
              onClick={handleSaveAndRelease}
              disabled={isBusy || !displayLetter?.id}>
              {intl.formatMessage({ id: 'save-and-release' })}
            </Button>
          )}
        </Fragment>,
      ]}
      onCancel={handleRequestClose}
      {...modalProps}>
      <Tabs activeKey={activeTab} onChange={handleTabChange} css={styles.tabs}>
        <Tabs.TabPane
          key="letter"
          tab={intl.formatMessage({
            id: isInstantMessage
              ? 'message-draft'
              : readOnly
              ? 'correction-letter-details'
              : 'correction-letter-tab-edit',
          })}>
          {isInstantMessage ? (
            <InstantMessageLetterTab
              ref={instantMessageTabRef}
              loading={readOnly && fullInstantMessageQuery.isLoading}
              error={false}
              isBusy={isBusy}
              projectId={projectId}
              tenderId={tenderId}
              shareId={shareId}
              shareKey={shareKey}
              messageData={readOnly ? (displayLetter as any) : localDraft!}
              onDirtyChange={setIsLetterDirty}
              resetNonce={letterResetNonce}
              readOnly={readOnly}
              onSaveTitleMessage={(payload) => {
                if (localDraft?.id) {
                  doUpdateDraft({
                    projectId,
                    tenderId,
                    draftId: localDraft.id,
                    body: payload,
                  });
                }
              }}
              onOpenFileByGuid={onOpenFileByGuid}
              onRefetch={async () => {
                queryCache.invalidateQueries([
                  'instant_message_drafts',
                  projectId,
                  tenderId,
                ]);

                if (localDraft?.id) {
                  try {
                    const freshDraft = await getInstantMessageDraft({
                      projectId,
                      tenderId,
                      draftId: localDraft.id,
                    });
                    setLocalDraft(freshDraft);
                  } catch (err) {
                    appLog('Failed to refetch draft', err);
                  }
                }
              }}
            />
          ) : (
            <LetterTab
              ref={letterTabRef}
              loading={
                (!readOnly && currentLetterQuery.isLoading) ||
                (readOnly && fullCorrectionLetterQuery.isLoading)
              }
              error={!readOnly && !!currentLetterQuery.error}
              letter={displayLetter}
              projectId={projectId}
              tenderId={tenderId}
              shareId={shareId}
              shareKey={shareKey}
              isBusy={isBusy}
              onDirtyChange={setIsLetterDirty}
              resetNonce={letterResetNonce}
              isInQaTab={isInQaTab}
              readOnly={readOnly}
              onSaveTitleDescription={(payload) => {
                doUpdateLetter({
                  projectId,
                  tenderId,
                  body: payload,
                });
              }}
              onOpenFileByGuid={onOpenFileByGuid}
              onRefetchLetter={() => currentLetterQuery.refetch()}
            />
          )}
        </Tabs.TabPane>

        {!isInstantMessage && (
          <Tabs.TabPane
            key="movements"
            tab={intl.formatMessage({ id: 'movement-items' })}>
            <MovementsTab
              loading={
                (!readOnly && currentLetterQuery.isLoading) ||
                (readOnly && fullCorrectionLetterQuery.isLoading)
              }
              error={!readOnly && !!currentLetterQuery.error}
              letter={displayLetter}
              projectId={projectId}
              tenderId={tenderId}
              isBusy={isBusy}
              readOnly={readOnly}
              shareId={shareId}
              shareKey={shareKey}
              onManageMovements={
                readOnly
                  ? undefined
                  : (action, movementIds) => {
                      doManageMovements({
                        projectId,
                        tenderId,
                        action,
                        body: { movement_ids: movementIds },
                      });
                    }
              }
            />
          </Tabs.TabPane>
        )}

        {!isInstantMessage && (
          <Tabs.TabPane
            key="questions"
            tab={intl.formatMessage({ id: 'correction-letter-qa-title' })}>
            <QuestionsTab
              loading={
                (!readOnly && currentLetterQuery.isLoading) ||
                (readOnly && fullCorrectionLetterQuery.isLoading)
              }
              error={!readOnly && !!currentLetterQuery.error}
              letter={displayLetter}
              projectId={projectId}
              tenderId={tenderId}
              isBusy={isBusy}
              readOnly={readOnly}
              shareId={shareId}
              shareKey={shareKey}
              onManageQuestions={
                readOnly
                  ? undefined
                  : (action, questionIds) => {
                      doManageQuestions({
                        projectId,
                        tenderId,
                        action,
                        body: { question_ids: questionIds },
                      });
                    }
              }
            />
          </Tabs.TabPane>
        )}

        <Tabs.TabPane
          key="preview"
          disabled={isPreviewDisabled}
          tab={
            isPreviewDisabled ? (
              <Tooltip title={intl.formatMessage({ id: 'will-be-available' })}>
                <span>
                  {intl.formatMessage({ id: 'correction-letter-tab-preview' })}
                </span>
              </Tooltip>
            ) : (
              intl.formatMessage({ id: 'correction-letter-tab-preview' })
            )
          }>
          <PreviewTab
            projectId={projectId}
            tenderId={tenderId}
            isInQaTab={isInQaTab}
            hideModeSelector={isInstantMessage || readOnly}
            isInstantMessage={
              isInstantMessage || (readOnly && readOnlyType === 5)
            }
            previewId={readOnlyId || localDraft?.id || displayLetter?.id}
            getPreview={(args) => {
              if (readOnly) {
                // === VENDOR SIDE ===
                if (isVendorSide && shareId && shareKey) {
                  // READONLY - Instant Message Vendor
                  if (readOnlyType === 5 && readOnlyId) {
                    return getSentInstantMessageEmailPreviewHtmlVendor({
                      shareId,
                      shareKey,
                      instantMessageId: readOnlyId,
                      lang: args.lang,
                    });
                  }
                  // READONLY - Correction Letter Vendor
                  if (readOnlyType === 2 && readOnlyId) {
                    return getSentCorrectionLetterEmailPreviewHtmlVendor({
                      shareId,
                      shareKey,
                      correctionLetterId: readOnlyId,
                      lang: args.lang,
                    });
                  }
                } else {
                  // === PROJECT SIDE ===
                  // READONLY - Instant Message Project
                  if (readOnlyType === 5 && readOnlyId) {
                    return getSentInstantMessageEmailPreviewHtml({
                      projectId: args.project_id,
                      tenderId: args.tender_id,
                      instantMessageId: readOnlyId,
                      lang: args.lang,
                    });
                  }
                  // READONLY - Correction Letter Project
                  if (readOnlyType === 2 && readOnlyId) {
                    return getSentCorrectionLetterEmailPreviewHtml({
                      project_id: args.project_id,
                      tender_id: args.tender_id,
                      tender_correction_letter_id: readOnlyId,
                      lang: args.lang,
                    });
                  }
                }
              } else {
                // === DRAFTS (Project Side) ===
                // DRAFT - Instant Message
                if (isInstantMessage && localDraft?.id) {
                  return getDraftInstantMessageEmailPreviewHtml({
                    projectId: args.project_id,
                    tenderId: args.tender_id,
                    draftId: localDraft.id,
                    lang: args.lang,
                  });
                }
              }
              // DRAFT - Correction Letter
              return getCurrentCorrectionLetterPreview(args);
            }}
            getModificationReportPreview={(args) =>
              getCurrentCorrectionLetterReportPreview(args)
            }
          />
        </Tabs.TabPane>
      </Tabs>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = () => ({
  modalTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  modalTitleLeft: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
  },
  titleText: {
    fontWeight: 600,
  },
  statusText: {
    whiteSpace: 'nowrap',
  },
  headerHint: {
    fontSize: 12,
    lineHeight: 1.3,
  },
  tabs: {
    marginTop: -8,
  },
});
